import DOMPurify from 'dompurify';
import { SymbolImportError, SYMBOL_LIMITS, validateSymbolDimensions } from './model';

const NS = 'http://www.w3.org/2000/svg';
const tags = [
  'svg',
  'g',
  'path',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
  'title',
  'desc',
];
const presentation = [
  'fill',
  'stroke',
  'color',
  'fill-opacity',
  'stroke-opacity',
  'opacity',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'fill-rule',
  'clip-rule',
  'stroke-dasharray',
  'stroke-dashoffset',
];
const geometry: Readonly<Record<string, readonly string[]>> = {
  svg: ['viewBox', 'width', 'height', 'preserveAspectRatio'],
  g: [],
  path: ['d'],
  rect: ['x', 'y', 'width', 'height', 'rx', 'ry'],
  circle: ['cx', 'cy', 'r'],
  ellipse: ['cx', 'cy', 'rx', 'ry'],
  line: ['x1', 'y1', 'x2', 'y2'],
  polyline: ['points'],
  polygon: ['points'],
  title: [],
  desc: [],
};
const numberPattern = /[-+]?(?:\d*\.\d+|\d+\.?\d*)(?:[eE][-+]?\d+)?/g;
const unsupported = () =>
  new SymbolImportError(
    'This SVG uses unsupported content or visual effects. Try exporting a transparent PNG.',
  );
const complexity = () =>
  new SymbolImportError(
    'This SVG exceeds Placeio’s complexity limits. Simplify its paths or export a transparent PNG.',
  );
function unsupportedElement(tag: string): SymbolImportError {
  if (['script', 'foreignObject'].includes(tag))
    return new SymbolImportError('This SVG contains executable or unsafe content.');
  if (tag === 'image')
    return new SymbolImportError(
      'This SVG uses a linked image, which Placeio does not currently support. Export it as a transparent PNG.',
    );
  if (tag === 'filter' || tag.startsWith('fe'))
    return new SymbolImportError(
      'This SVG uses a filter effect that cannot be imported safely. Export a simplified SVG or transparent PNG.',
    );
  if (tag === 'style')
    return new SymbolImportError(
      'This SVG uses unsupported CSS styling. Export with presentation attributes or as a transparent PNG.',
    );
  if (
    [
      'defs',
      'use',
      'linearGradient',
      'radialGradient',
      'clipPath',
      'mask',
      'pattern',
      'symbol',
    ].includes(tag)
  )
    return new SymbolImportError(
      `This SVG uses unsupported ${tag} artwork. Export a flattened SVG with paths and literal colours, or a transparent PNG.`,
    );
  if (tag === 'text')
    return new SymbolImportError(
      'This SVG contains text. Convert text to paths or export a transparent PNG.',
    );
  return unsupported();
}
interface Budget {
  elements: number;
  numbers: number;
  commands: number;
}
function numbers(value: string, budget: Budget): number[] {
  const found = value.match(numberPattern) ?? [];
  if (value.replace(numberPattern, '').replace(/[\s,]/g, '') || !found.length) throw unsupported();
  budget.numbers += found.length;
  const result = found.map(Number);
  if (
    budget.numbers > SYMBOL_LIMITS.numbers ||
    result.some((n) => !Number.isFinite(n) || Math.abs(n) > 1_000_000)
  )
    throw complexity();
  return result;
}
function path(value: string, budget: Budget): void {
  const parts = value.match(/[a-df-zA-DF-Z]|[-+]?(?:\d*\.\d+|\d+\.?\d*)(?:[eE][-+]?\d+)?/g) ?? [];
  if (
    value.replace(/[a-df-zA-DF-Z]|[-+]?(?:\d*\.\d+|\d+\.?\d*)(?:[eE][-+]?\d+)?|[\s,]/g, '') ||
    !/^[Mm]$/.test(parts[0] ?? '')
  )
    throw unsupported();
  const arities: Readonly<Record<string, number>> = {
    m: 2,
    l: 2,
    h: 1,
    v: 1,
    c: 6,
    s: 4,
    q: 4,
    t: 2,
    a: 7,
    z: 0,
  };
  let i = 0;
  while (i < parts.length) {
    const command = parts[i++]!.toLowerCase(),
      count = arities[command];
    if (count === undefined) throw unsupported();
    if (count === 0) {
      budget.commands++;
      if (budget.commands > SYMBOL_LIMITS.commands) throw complexity();
      continue;
    }
    let groups = 0;
    while (i < parts.length && !/^[a-z]$/i.test(parts[i]!)) {
      const args: string[] = parts.slice(i, i + count);
      if (args.length !== count || args.some((n) => /^[a-z]$/i.test(n))) throw unsupported();
      const values = numbers(args.join(' '), budget);
      if (
        command === 'a' &&
        (values[0]! < 0 ||
          values[1]! < 0 ||
          ![0, 1].includes(values[3]!) ||
          ![0, 1].includes(values[4]!))
      )
        throw unsupported();
      i += count;
      groups++;
      budget.commands++;
    }
    if (!groups) throw unsupported();
    if (budget.commands > SYMBOL_LIMITS.commands) throw complexity();
  }
}
function transform(value: string, budget: Budget): number {
  let scale = 1;
  const pattern = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^()]*)\)/g;
  let rest = value,
    matched = false;
  for (const match of value.matchAll(pattern)) {
    matched = true;
    rest = rest.replace(match[0], '');
    const values = numbers(match[2]!, budget),
      name = match[1]!;
    const valid =
      name === 'matrix'
        ? values.length === 6
        : name === 'rotate'
          ? [1, 3].includes(values.length)
          : name.startsWith('skew')
            ? values.length === 1
            : [1, 2].includes(values.length);
    if (!valid) throw unsupported();
    // Conservative composite coefficient budget, including inherited transforms.
    scale *=
      name === 'matrix'
        ? Math.max(1, ...values.map(Math.abs)) * 3
        : name === 'scale'
          ? Math.max(1, ...values.map(Math.abs))
          : name.startsWith('skew')
            ? 1 + Math.abs(Math.tan((values[0]! * Math.PI) / 180))
            : 2;
  }
  if (!matched || rest.replace(/[\s,]/g, '') || !Number.isFinite(scale) || scale > 1_000_000)
    throw complexity();
  return scale;
}
function parse(text: string): Document {
  // XML declaration is harmless; all other processing instructions, DTDs and entities are forbidden.
  text = text.replace(/^\uFEFF?\s*<\?xml\s+[^?]*\?>/i, '');
  if (/<!(?:DOCTYPE|ENTITY)|<\?/i.test(text))
    throw new SymbolImportError(
      /<!(?:DOCTYPE|ENTITY)/i.test(text)
        ? 'This SVG contains a DTD or entity declaration. Re-export it without document declarations, or use a transparent PNG.'
        : 'This SVG contains a processing instruction or external stylesheet. Export a self-contained SVG or transparent PNG.',
    );
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  if (
    doc.querySelector('parsererror') ||
    doc.documentElement.localName !== 'svg' ||
    doc.documentElement.namespaceURI !== NS
  )
    throw new SymbolImportError('This file is not a valid SVG document.');
  return doc;
}
function normalize(doc: Document): { width: number; height: number } {
  const budget: Budget = { elements: 0, numbers: 0, commands: 0 };
  const walk = (
    el: Element,
    depth: number,
    inheritedScale: number,
    inheritedColour = '#000000',
  ) => {
    if (++budget.elements > SYMBOL_LIMITS.elements || depth > SYMBOL_LIMITS.depth)
      throw complexity();
    if (el.namespaceURI !== NS)
      throw new SymbolImportError(
        'This SVG contains an unsupported namespace. Export a plain SVG or transparent PNG.',
      );
    if (!tags.includes(el.localName) || (depth > 1 && el.localName === 'svg'))
      throw unsupportedElement(el.localName);
    if (el.prefix) {
      const unprefixed = doc.createElementNS(NS, el.localName);
      for (const attr of [...el.attributes])
        unprefixed.setAttributeNS(attr.namespaceURI, attr.name, attr.value);
      while (el.firstChild) unprefixed.appendChild(el.firstChild);
      el.replaceWith(unprefixed);
      el = unprefixed;
    }
    // Nonvisual exporter metadata is safely discarded, never interpreted as artwork.
    if (depth === 1) {
      for (const key of ['x', 'y']) {
        if (el.hasAttribute(key)) {
          if (Number(el.getAttribute(key)) !== 0) throw unsupported();
          el.removeAttribute(key);
        }
      }
    }
    if (el.hasAttribute('style')) {
      // Normalize only static presentation declarations, never selectors, variables or URL paints.
      for (const declaration of el
        .getAttribute('style')!
        .split(';')
        .filter((s) => s.trim())) {
        const match = /^\s*([a-z-]+)\s*:\s*([^:;{}!]+)\s*$/.exec(declaration);
        if (
          match?.[1] === 'enable-background' &&
          el.localName === 'svg' &&
          /^new\s+[-\d.\s]+$/.test(match[2]!)
        )
          continue;
        if (!match || !presentation.includes(match[1]!)) throw unsupportedElement('style');
        el.setAttribute(match[1]!, match[2]!.trim());
      }
      el.removeAttribute('style');
    }
    const colour =
      el.getAttribute('color') && el.getAttribute('color') !== 'currentColor'
        ? el.getAttribute('color')!
        : inheritedColour;
    for (const attr of [...el.attributes]) {
      const key = attr.name,
        value = attr.value.trim();
      if (value.length > SYMBOL_LIMITS.attributeLength) throw unsupported();
      if (/^on/i.test(key))
        throw new SymbolImportError('This SVG contains executable or unsafe content.');
      if (['href', 'xlink:href'].includes(key))
        throw new SymbolImportError(
          'This SVG contains external or linked resources and cannot be imported. Export a flattened SVG or transparent PNG.',
        );
      // Accessibility/exporter labels have no visual effect in our decoded image. Discard
      // them before the presentation allowlist; they are never interpreted or put in the UI.
      if (
        !attr.namespaceURI &&
        (['role', 'focusable', 'tabindex', 'class'].includes(key) ||
          /^aria-[a-z]+(?:-[a-z]+)*$/.test(key) ||
          /^data-[a-z][a-z0-9-]*$/.test(key))
      ) {
        el.removeAttribute(key);
        continue;
      }
      if (key === 'xmlns' && value === NS) continue;
      if (
        depth === 1 &&
        ((key === 'baseProfile' && ['tiny', 'basic', 'full'].includes(value)) ||
          (key === 'overflow' && ['inherit', 'hidden'].includes(value)))
      ) {
        el.removeAttribute(key);
        continue;
      }
      if (
        ['id', 'version'].includes(key) ||
        key.startsWith('xmlns:') ||
        (key === 'xml:space' && ['preserve', 'default'].includes(value)) ||
        [
          'http://www.inkscape.org/namespaces/inkscape',
          'http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd',
        ].includes(attr.namespaceURI ?? '')
      ) {
        el.removeAttribute(key);
        continue;
      }
      if (
        attr.namespaceURI ||
        (!presentation.includes(key) &&
          !geometry[el.localName]!.includes(key) &&
          key !== 'transform')
      )
        throw new SymbolImportError(
          'This SVG uses an unsupported attribute. Export a plain SVG or transparent PNG.',
        );
      if (/url\s*\(|@|[<>\\]/i.test(value))
        throw new SymbolImportError(
          'This SVG contains external or URL-valued resources and cannot be imported. Export a flattened SVG or transparent PNG.',
        );
      if (['fill', 'stroke', 'color'].includes(key)) {
        if (
          !/^(?:none|currentColor|transparent|#[\da-f]{3,8}|[a-z]{1,24}|rgba?\([\d\s.,%]+\))$/i.test(
            value,
          )
        )
          throw unsupported();
        if (value === 'currentColor') el.setAttribute(key, colour);
      } else if (key === 'd') path(value, budget);
      else if (key === 'transform') {
        /* validated with inherited transform below */
      } else if (
        [
          'stroke-linecap',
          'stroke-linejoin',
          'fill-rule',
          'clip-rule',
          'preserveAspectRatio',
        ].includes(key)
      ) {
        const allowed: Readonly<Record<string, readonly string[]>> = {
          'stroke-linecap': ['butt', 'round', 'square'],
          'stroke-linejoin': ['miter', 'round', 'bevel'],
          'fill-rule': ['nonzero', 'evenodd'],
          'clip-rule': ['nonzero', 'evenodd'],
          preserveAspectRatio: ['xMidYMid meet', 'xMidYMid'],
        };
        if (!allowed[key]!.includes(value)) throw unsupported();
      } else if (key === 'stroke-dasharray' && value === 'none') {
        /* static solid stroke */
      } else {
        let numeric = value.replace(/px$/, '');
        if (depth === 1 && ['width', 'height'].includes(key)) {
          const length = /^([\d.]+)(px|pt|pc|mm|cm|in|%)?$/.exec(value);
          if (
            !length ||
            !(Number(length[1]) > 0) ||
            (length[2] === '%' && !el.hasAttribute('viewBox'))
          )
            throw new SymbolImportError('This SVG has invalid dimensions or no usable viewBox.');
          const units: Record<string, number> = {
            px: 1,
            pt: 96 / 72,
            pc: 16,
            mm: 96 / 25.4,
            cm: 96 / 2.54,
            in: 96,
            '%': 1,
          };
          numeric = String(Number(length[1]) * (units[length[2] ?? 'px'] ?? 1));
          el.setAttribute(key, numeric);
        }
        const values = numbers(numeric, budget);
        if (
          ['opacity', 'fill-opacity', 'stroke-opacity'].includes(key) &&
          (values.length !== 1 || values[0]! < 0 || values[0]! > 1)
        )
          throw unsupported();
        if (
          key === 'viewBox'
            ? values.length !== 4
            : key === 'points'
              ? values.length % 2 !== 0
              : key === 'stroke-dasharray'
                ? values.some((n) => n < 0)
                : values.length !== 1
        )
          throw unsupported();
        if (
          ['width', 'height', 'r', 'rx', 'ry', 'stroke-width', 'stroke-miterlimit'].includes(key) &&
          values.some((n) => n < 0)
        )
          throw unsupported();
      }
    }
    const scale =
      inheritedScale *
      (el.hasAttribute('transform') ? transform(el.getAttribute('transform')!, budget) : 1);
    if (scale > 1_000_000) throw complexity();
    for (const child of [...el.childNodes]) {
      if (child.nodeType === 1) {
        if (
          !['svg', 'g'].includes(el.localName) &&
          !['title', 'desc'].includes((child as Element).localName)
        )
          throw unsupported();
        const element = child as Element;
        if (
          el.localName === 'svg' &&
          element.localName === 'metadata' &&
          element.namespaceURI === NS
        ) {
          const pending = [{ node: element, depth: depth + 1 }];
          while (pending.length) {
            const { node, depth: metadataDepth } = pending.pop()!;
            if (++budget.elements > SYMBOL_LIMITS.elements || metadataDepth > SYMBOL_LIMITS.depth)
              throw complexity();
            if ([...node.attributes].some((a) => a.value.length > SYMBOL_LIMITS.attributeLength))
              throw complexity();
            if (
              ['script', 'foreignObject', 'image'].includes(node.localName) ||
              [...node.attributes].some((a) => /^on|href$/i.test(a.localName))
            )
              throw unsupportedElement('script');
            for (const child of node.children)
              pending.push({ node: child, depth: metadataDepth + 1 });
          }
          element.remove();
        } else walk(element, depth + 1, scale, colour);
      } else if (child.nodeType === 8) child.remove();
      else if (
        child.nodeType !== 3 ||
        (child.textContent?.trim() && !['title', 'desc'].includes(el.localName))
      )
        throw unsupported();
    }
  };
  walk(doc.documentElement, 1, 1);
  const root = doc.documentElement;
  const box = root.hasAttribute('viewBox')
    ? numbers(root.getAttribute('viewBox')!, budget)
    : [
        0,
        0,
        Number(root.getAttribute('width')?.replace(/px$/, '')),
        Number(root.getAttribute('height')?.replace(/px$/, '')),
      ];
  const width = box[2]!,
    height = box[3]!;
  validateSymbolDimensions(width, height);
  if (width < 0.0001 || height < 0.0001 || width > 1_000_000 || height > 1_000_000)
    throw unsupported();
  root.setAttribute('viewBox', box.join(' '));
  root.setAttribute('width', String((width * 128) / Math.max(width, height)));
  root.setAttribute('height', String((height * 128) / Math.max(width, height)));
  root.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  return { width, height };
}
/** Sanitization may normalize syntax, but must not silently discard supported artwork. */
function artworkSignature(root: Element): string {
  return JSON.stringify(
    [root, ...root.querySelectorAll('*')].map((el) => [
      el.localName,
      el.childElementCount,
      [...el.attributes]
        .filter((a) => a.namespaceURI !== 'http://www.w3.org/2000/xmlns/' && a.name !== 'xmlns')
        .map((a) => [
          a.name,
          a.name === 'd'
            ? (
                a.value.match(/[a-df-zA-DF-Z]|[-+]?(?:\d*\.\d+|\d+\.?\d*)(?:[eE][-+]?\d+)?/g) ?? []
              ).join(' ')
            : a.value.replace(/\s+/g, ' ').trim(),
        ])
        .sort(([a], [b]) => a!.localeCompare(b!)),
    ]),
  );
}
export function sanitizeSymbolSvg(bytes: Uint8Array): {
  bytes: Uint8Array;
  width: number;
  height: number;
} {
  if (!bytes.length || bytes.length > SYMBOL_LIMITS.svgBytes)
    throw new SymbolImportError('This SVG is too large. Use at most 512 KiB.');
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new SymbolImportError('This SVG is not valid UTF-8 text.');
  }
  const doc = parse(text);
  normalize(doc);
  const normalized = new XMLSerializer().serializeToString(doc.documentElement);
  const purified = DOMPurify.sanitize(normalized, {
    ALLOWED_TAGS: tags,
    ALLOWED_ATTR: [...presentation, 'xmlns', 'transform', ...Object.values(geometry).flat()],
    NAMESPACE: NS,
    PARSER_MEDIA_TYPE: 'application/xhtml+xml',
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
  const final = parse(purified);
  const dimensions = normalize(final);
  if (artworkSignature(doc.documentElement) !== artworkSignature(final.documentElement))
    throw new SymbolImportError(
      'This SVG could not be sanitized without changing its artwork. Export a flattened SVG or transparent PNG.',
    );
  const result = new TextEncoder().encode(
    new XMLSerializer().serializeToString(final.documentElement),
  );
  if (result.length > SYMBOL_LIMITS.svgBytes) throw unsupported();
  // Validate the exact final bytes again. No consumer may subsequently edit this source.
  normalize(parse(new TextDecoder().decode(result)));
  return { bytes: result, ...dimensions };
}
